import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthCommsDistributionsTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { DistributionRecipientSummary, DistributionRun, GrowthCommsDistributionService } from './growth-comms-distribution.service';

@Component({
  standalone: true,
  selector: 'ain-growth-comms-distribution-detail',
  providers: [ConfirmationService],
  imports: [RouterLink, AinPageComponent, AinStateComponent, InfoHintComponent, ButtonModule, TableModule, ConfirmDialogModule, AinCanAccessDirective],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="run()?.subject || t('growth.distribution.detail.title')" [description]="t('growth.distribution.detail.description')">
      <div class="toolbar">
        <a routerLink="/growth/comms/distributions" pButton severity="secondary" [label]="t('common.back')"></a>
        @if (run()) {
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.distribution.manage'" pButton type="button" [label]="t('growth.distribution.actions.preview')" (click)="preview()"></button>
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.distribution.approve'" pButton type="button" severity="help" [label]="t('growth.distribution.actions.submitApproval')" (click)="submitApproval()"></button>
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.distribution.dispatch'" pButton type="button" [label]="t('growth.distribution.actions.dispatch')" (click)="dispatch()"></button>
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.distribution.manage'" pButton type="button" severity="danger" [label]="t('growth.distribution.actions.cancel')" (click)="confirmCancel()"></button>
        }
      </div>

      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (!run()) { <ain-state type="empty" [title]="t('common.notFound')" /> }
      @else {
        <section class="card">
          <ain-info-hint [titleKey]="'growth.distribution.help.detail.title'" [descriptionKey]="'growth.distribution.help.detail.description'" />
          <div class="counters">
            <span>{{ t('columns.status') }}: <strong>{{ run()!.status }}</strong></span>
            <span>{{ t('growth.distribution.counters.total') }}: {{ run()!.totalCount }}</span>
            <span>{{ t('growth.distribution.counters.eligible') }}: {{ run()!.eligibleCount }}</span>
            <span>{{ t('growth.distribution.counters.suppressed') }}: {{ run()!.suppressedCount }}</span>
            <span>{{ t('growth.distribution.counters.sent') }}: {{ run()!.sentCount }}</span>
            <span>{{ t('growth.distribution.counters.failed') }}: {{ run()!.failedCount }}</span>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.distribution.sections.recipients') }}</h3>
          <p-table [value]="recipients()" [lazy]="true" [paginator]="true" [rows]="20" [totalRecords]="recipientTotal()" (onLazyLoad)="onRecipientsLazyLoad($event)">
            <ng-template pTemplate="header"><tr><th>{{ t('notifications.messageDetail.columns.party') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('growth.distribution.columns.reason') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.partyDisplayName }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.previewReasonCode || '—' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`.toolbar{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}.counters{display:flex;gap:12px;flex-wrap:wrap;margin-top:8px}`]
})
export class GrowthCommsDistributionDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(GrowthCommsDistributionService);
  private readonly i18n = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly run = signal<DistributionRun | null>(null);
  readonly recipients = signal<DistributionRecipientSummary[]>([]);
  readonly recipientTotal = signal(0);

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthCommsDistributionChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const runId = params.get('runId');
      if (runId) this.load(true, runId);
    });
  }

  t(key: string): string { return this.i18n.translate(key); }

  preview(): void {
    if (!this.canAccess('growth.comms.distribution.manage')) return;
    const runId = this.run()?.id;
    if (!runId) return;
    this.api.previewRun(runId).subscribe(() => this.load(false, runId));
  }

  submitApproval(): void {
    if (!this.canAccess('growth.comms.distribution.approve')) return;
    const runId = this.run()?.id;
    if (!runId) return;
    this.api.submitApproval(runId).subscribe(run => this.run.set(run));
  }

  dispatch(): void {
    if (!this.canAccess('growth.comms.distribution.dispatch')) return;
    const runId = this.run()?.id;
    if (!runId) return;
    this.api.dispatch(runId).subscribe(run => this.run.set(run));
  }

  confirmCancel(): void {
    if (!this.canAccess('growth.comms.distribution.manage')) return;
    const runId = this.run()?.id;
    if (!runId) return;
    this.confirmation.confirm({
      header: this.t('growth.distribution.actions.cancel'),
      message: this.t('growth.distribution.confirm.cancel'),
      accept: () => this.api.cancel(runId).subscribe(run => this.run.set(run))
    });
  }

  private load(showLoading: boolean, runId = this.route.snapshot.paramMap.get('runId') ?? ''): void {
    if (!runId) return;
    if (showLoading) this.loading.set(true);
    this.api.getRun(runId).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: run => this.run.set(run)
    });
    this.loadRecipients(runId, 0, 20);
  }

  onRecipientsLazyLoad(event: TableLazyLoadEvent): void {
    const runId = this.run()?.id;
    if (runId) this.loadRecipients(runId, event.first ?? 0, event.rows ?? 20);
  }

  private loadRecipients(runId: string, skip: number, take: number): void {
    this.api.listRecipients(runId, skip, take).subscribe(page => {
      this.recipients.set(page.items);
      this.recipientTotal.set(page.totalCount);
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(growthCommsDistributionsTopic(tenantId));
  }

  private canAccess(permission: string): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === permission);
  }
}
